import {
  fromMillidinars,
  sum,
  type Millidinars,
  type OutstandingSessionRow,
  type OutstandingTabDto,
} from '@gc/shared';

/**
 * What the cashier has ticked on the Outstanding screen.
 *
 * Rows come from two different tables — sessions and order bills — and the ids
 * cannot be pooled: a session id and a bill id are both opaque strings, and
 * passing one where the other belongs is exactly the mistake the server refuses
 * with `order_bill_not_on_tab`. So the selection is two sets, kept apart all the
 * way to the wire.
 *
 * The third piece is item grain (owner, 2026-08-19: "we should be able to pay
 * for only one of the products"): `orderItemIds` holds the PARTIALLY selected
 * orders, bill id → the item lines ticked. The invariant the toggles below
 * maintain is that a bill is never in both places — every line ticked promotes
 * it into `orderBillIds` (the whole bill, settled without a split), and
 * unticking one line of a whole bill demotes it back. The Pay flow turns each
 * `orderItemIds` entry into one `split-order-bill` call before checkout.
 *
 * Pure and separate from the page for the usual reason: the money the bottom bar
 * quotes is derived here, and it has to be testable without a DOM.
 */
export interface OutstandingSelection {
  sessionIds: ReadonlySet<string>;
  orderBillIds: ReadonlySet<string>;
  /** Partially selected orders: bill id → ticked item lines. */
  orderItemIds: ReadonlyMap<string, ReadonlySet<string>>;
}

export const EMPTY_SELECTION: OutstandingSelection = {
  sessionIds: new Set(),
  orderBillIds: new Set(),
  orderItemIds: new Map(),
};

export function selectionSize(selection: OutstandingSelection): number {
  let items = 0;
  for (const ids of selection.orderItemIds.values()) items += ids.size;
  return selection.sessionIds.size + selection.orderBillIds.size + items;
}

function toggle(ids: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(ids);
  if (!next.delete(id)) next.add(id);
  return next;
}

function without(ids: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(ids);
  next.delete(id);
  return next;
}

function withEntry(
  map: ReadonlyMap<string, ReadonlySet<string>>,
  billId: string,
  itemIds: ReadonlySet<string>,
): Map<string, ReadonlySet<string>> {
  const next = new Map(map);
  if (itemIds.size === 0) next.delete(billId);
  else next.set(billId, itemIds);
  return next;
}

function withoutEntry(
  map: ReadonlyMap<string, ReadonlySet<string>>,
  billId: string,
): ReadonlyMap<string, ReadonlySet<string>> {
  if (!map.has(billId)) return map;
  const next = new Map(map);
  next.delete(billId);
  return next;
}

export function toggleSession(
  selection: OutstandingSelection,
  sessionId: string,
): OutstandingSelection {
  return { ...selection, sessionIds: toggle(selection.sessionIds, sessionId) };
}

/**
 * The bill row's own checkbox: whole-bill semantics. Half-ticked (some items in
 * the map) counts as "not whole", so the first click completes the bill rather
 * than clearing it — the box shows an indeterminate dash in that state, and
 * completing is what a dash-click means everywhere else on the screen.
 */
export function toggleOrder(selection: OutstandingSelection, billId: string): OutstandingSelection {
  const wasWhole = selection.orderBillIds.has(billId);
  return {
    ...selection,
    orderBillIds: wasWhole
      ? without(selection.orderBillIds, billId)
      : new Set([...selection.orderBillIds, billId]),
    // Either way the partial entry goes: promoted into the whole bill, or
    // cleared with it.
    orderItemIds: withoutEntry(selection.orderItemIds, billId),
  };
}

/** The order a single item tick needs to know: its bill and all of its lines. */
export interface TickableOrder {
  billId: string;
  itemIds: readonly string[];
}

/**
 * One item line's checkbox.
 *
 * Keeps the invariant: ticking the last unticked line promotes the bill to a
 * whole selection (settled with NO split), and unticking one line of a whole
 * bill demotes it to a partial holding the rest.
 */
export function toggleOrderItem(
  selection: OutstandingSelection,
  order: TickableOrder,
  orderItemId: string,
): OutstandingSelection {
  if (selection.orderBillIds.has(order.billId)) {
    const rest = new Set(order.itemIds.filter((id) => id !== orderItemId));
    return {
      ...selection,
      orderBillIds: without(selection.orderBillIds, order.billId),
      orderItemIds: withEntry(selection.orderItemIds, order.billId, rest),
    };
  }

  const ticked = toggle(selection.orderItemIds.get(order.billId) ?? new Set(), orderItemId);
  const whole = order.itemIds.length > 0 && order.itemIds.every((id) => ticked.has(id));
  if (whole) {
    return {
      ...selection,
      orderBillIds: new Set([...selection.orderBillIds, order.billId]),
      orderItemIds: withoutEntry(selection.orderItemIds, order.billId),
    };
  }
  return { ...selection, orderItemIds: withEntry(selection.orderItemIds, order.billId, ticked) };
}

/** True when this bill has SOME lines ticked but not the whole bill. */
export function orderPartlySelected(selection: OutstandingSelection, billId: string): boolean {
  return !selection.orderBillIds.has(billId) && selection.orderItemIds.has(billId);
}

/** Whole bill ticked, or this particular line within a partial one. */
export function orderItemSelected(
  selection: OutstandingSelection,
  billId: string,
  orderItemId: string,
): boolean {
  if (selection.orderBillIds.has(billId)) return true;
  return selection.orderItemIds.get(billId)?.has(orderItemId) ?? false;
}

/** Every row of one party — the "he's paying for all of it" case. */
export function selectWholeTab(
  selection: OutstandingSelection,
  tab: OutstandingTabDto,
): OutstandingSelection {
  const sessionIds = new Set(selection.sessionIds);
  const orderBillIds = new Set(selection.orderBillIds);
  let orderItemIds: ReadonlyMap<string, ReadonlySet<string>> = selection.orderItemIds;
  for (const session of tab.sessions) sessionIds.add(session.sessionId);
  for (const order of tab.orders) {
    orderBillIds.add(order.billId);
    orderItemIds = withoutEntry(orderItemIds, order.billId);
  }
  return { sessionIds, orderBillIds, orderItemIds };
}

/**
 * One row, reduced to what selecting it needs. Structurally what
 * `OutstandingLine` already is, restated here so this module keeps knowing
 * nothing about the table.
 */
export interface SelectableLine {
  kind: 'session' | 'order';
  id: string;
}

/**
 * Tick every one of these rows — the header checkbox (owner request: "a
 * checkbox that will select all of the things of that person").
 *
 * Takes the rows the cashier can SEE, never a whole tab, and that distinction
 * is the point. The screen filters by visitor, by category and by search, so a
 * select-all built on `selectWholeTab` would tick rows scrolled out of sight
 * behind a filter and hand them to a settlement nobody read. What is on screen
 * is what gets charged.
 *
 * Whole-bill semantics for orders: a half-ticked order completes, absorbing its
 * partial entry.
 */
export function selectLines(
  selection: OutstandingSelection,
  lines: readonly SelectableLine[],
): OutstandingSelection {
  const sessionIds = new Set(selection.sessionIds);
  const orderBillIds = new Set(selection.orderBillIds);
  let orderItemIds: ReadonlyMap<string, ReadonlySet<string>> = selection.orderItemIds;
  for (const line of lines) {
    if (line.kind === 'session') sessionIds.add(line.id);
    else {
      orderBillIds.add(line.id);
      orderItemIds = withoutEntry(orderItemIds, line.id);
    }
  }
  return { sessionIds, orderBillIds, orderItemIds };
}

/** Untick exactly these rows — partial item ticks included — leaving any selection off-screen alone. */
export function clearLines(
  selection: OutstandingSelection,
  lines: readonly SelectableLine[],
): OutstandingSelection {
  const sessionIds = new Set(selection.sessionIds);
  const orderBillIds = new Set(selection.orderBillIds);
  let orderItemIds: ReadonlyMap<string, ReadonlySet<string>> = selection.orderItemIds;
  for (const line of lines) {
    if (line.kind === 'session') sessionIds.delete(line.id);
    else {
      orderBillIds.delete(line.id);
      orderItemIds = withoutEntry(orderItemIds, line.id);
    }
  }
  return { sessionIds, orderBillIds, orderItemIds };
}

/**
 * True when every given row is ticked WHOLE. Empty is false — nothing to tick.
 * A partially ticked order does not count: the header box must never claim a
 * section is fully selected while a Pay would leave a pepsi behind.
 */
export function linesFullySelected(
  selection: OutstandingSelection,
  lines: readonly SelectableLine[],
): boolean {
  if (lines.length === 0) return false;
  return lines.every((line) =>
    line.kind === 'session' ? selection.sessionIds.has(line.id) : selection.orderBillIds.has(line.id),
  );
}

/** Some but not all — drives the header box's indeterminate dash. */
export function linesPartlySelected(
  selection: OutstandingSelection,
  lines: readonly SelectableLine[],
): boolean {
  const picked = lines.filter((line) =>
    line.kind === 'session' ? selection.sessionIds.has(line.id) : selection.orderBillIds.has(line.id),
  ).length;
  if (picked > 0 && picked < lines.length) return true;
  // A partial order alone also reads as "partly": something in this section is
  // ticked even though no row is ticked whole.
  const anyPartial = lines.some(
    (line) => line.kind === 'order' && orderPartlySelected(selection, line.id),
  );
  return anyPartial && picked < lines.length;
}

export function clearTab(
  selection: OutstandingSelection,
  tab: OutstandingTabDto,
): OutstandingSelection {
  const sessionIds = new Set(selection.sessionIds);
  const orderBillIds = new Set(selection.orderBillIds);
  let orderItemIds: ReadonlyMap<string, ReadonlySet<string>> = selection.orderItemIds;
  for (const session of tab.sessions) sessionIds.delete(session.sessionId);
  for (const order of tab.orders) {
    orderBillIds.delete(order.billId);
    orderItemIds = withoutEntry(orderItemIds, order.billId);
  }
  return { sessionIds, orderBillIds, orderItemIds };
}

export function tabFullySelected(selection: OutstandingSelection, tab: OutstandingTabDto): boolean {
  if (tab.sessions.length + tab.orders.length === 0) return false;
  return (
    tab.sessions.every((session) => selection.sessionIds.has(session.sessionId)) &&
    tab.orders.every((order) => selection.orderBillIds.has(order.billId))
  );
}

/**
 * Drop ids that are no longer on screen.
 *
 * The screen polls, and a row vanishes the moment another till settles it. A
 * selection holding a settled session would send it to checkout and earn a
 * `session_already_settled` refusal for the whole party — so the pruning happens
 * here, and identity is preserved when nothing changed so the poll costs no
 * renders.
 *
 * Item ticks are pruned at both grains: the bill must still exist, and each
 * ticked line must still be on it — a refund or another till's split can take a
 * line away mid-selection.
 */
export function pruneSelection(
  selection: OutstandingSelection,
  tabs: readonly OutstandingTabDto[],
): OutstandingSelection {
  const liveSessions = new Set(tabs.flatMap((tab) => tab.sessions.map((s) => s.sessionId)));
  const liveOrders = new Map(
    tabs.flatMap((tab) =>
      tab.orders.map(
        (o) => [o.billId, new Set(o.items.map((item) => item.orderItemId))] as const,
      ),
    ),
  );

  const sessionIds = [...selection.sessionIds].filter((id) => liveSessions.has(id));
  const orderBillIds = [...selection.orderBillIds].filter((id) => liveOrders.has(id));

  let itemsChanged = false;
  const orderItemIds = new Map<string, ReadonlySet<string>>();
  for (const [billId, itemIds] of selection.orderItemIds) {
    const liveItems = liveOrders.get(billId);
    if (!liveItems) {
      itemsChanged = true;
      continue;
    }
    const kept = [...itemIds].filter((id) => liveItems.has(id));
    if (kept.length !== itemIds.size) itemsChanged = true;
    if (kept.length > 0) orderItemIds.set(billId, new Set(kept));
  }

  if (
    sessionIds.length === selection.sessionIds.size &&
    orderBillIds.length === selection.orderBillIds.size &&
    !itemsChanged
  ) {
    return selection;
  }
  return { sessionIds: new Set(sessionIds), orderBillIds: new Set(orderBillIds), orderItemIds };
}

/**
 * The selection grouped by party, because settlement is per party: five friends
 * on two different tabs is two receipts and two handovers, never one.
 */
export interface SelectedTabGroup {
  tab: OutstandingTabDto;
  sessionIds: string[];
  orderBillIds: string[];
  /**
   * The partial orders, ready for `POST /tabs/:id/split-order-bill` — one call
   * each, made by the Pay flow before the checkout drawer opens.
   */
  orderItemSplits: Array<{ billId: string; orderItemIds: string[] }>;
  /** What this party's selected rows come to, before any discount. */
  subtotalMd: Millidinars;
}

export function groupSelectionByTab(
  selection: OutstandingSelection,
  tabs: readonly OutstandingTabDto[],
): SelectedTabGroup[] {
  const groups: SelectedTabGroup[] = [];
  for (const tab of tabs) {
    const sessionIds = tab.sessions
      .filter((session) => selection.sessionIds.has(session.sessionId))
      .map((session) => session.sessionId);
    const orderBillIds = tab.orders
      .filter((order) => selection.orderBillIds.has(order.billId))
      .map((order) => order.billId);
    const orderItemSplits = tab.orders
      .filter((order) => orderPartlySelected(selection, order.billId))
      .map((order) => ({
        billId: order.billId,
        // Board order, matching the split endpoint's own load order.
        orderItemIds: order.items
          .filter((item) => selection.orderItemIds.get(order.billId)?.has(item.orderItemId))
          .map((item) => item.orderItemId),
      }))
      .filter((split) => split.orderItemIds.length > 0);
    if (sessionIds.length + orderBillIds.length + orderItemSplits.length === 0) continue;

    // Whole rows quote the row figure; ticked lines quote the server-computed
    // `owedMd`, so the bar's total equals what the split-then-settle charges.
    const partialTotals = tab.orders.flatMap((order) =>
      orderPartlySelected(selection, order.billId)
        ? order.items
            .filter((item) => selection.orderItemIds.get(order.billId)?.has(item.orderItemId))
            .map((item) => fromMillidinars(item.owedMd))
        : [],
    );

    groups.push({
      tab,
      sessionIds,
      orderBillIds,
      orderItemSplits,
      subtotalMd: sum([
        ...tab.sessions
          .filter((session) => selection.sessionIds.has(session.sessionId))
          .map((session) => fromMillidinars(session.totalMd)),
        ...tab.orders
          .filter((order) => selection.orderBillIds.has(order.billId))
          .map((order) => fromMillidinars(order.totalMd)),
        ...partialTotals,
      ]),
    });
  }
  return groups;
}

export function selectionTotalMd(
  selection: OutstandingSelection,
  tabs: readonly OutstandingTabDto[],
): Millidinars {
  return sum(groupSelectionByTab(selection, tabs).map((group) => group.subtotalMd));
}

/**
 * Sessions whose clock is still running, among those ticked — the ones "End" has
 * anything to do. A session already ended is left alone rather than refused:
 * ending it again is a no-op the cashier did not ask for.
 *
 * Each carries how its device is priced, because End asks a round-priced one
 * for its rounds (OW19) — and offers 3 ball only where the sitting has a
 * 3-ball price to bill them at.
 */
export function runningSelectedSessions(
  selection: OutstandingSelection,
  tabs: readonly OutstandingTabDto[],
): RunningSelectedSession[] {
  return tabs.flatMap((tab) =>
    tab.sessions
      .filter(
        (session) =>
          selection.sessionIds.has(session.sessionId) &&
          (session.status === 'ACTIVE' || session.status === 'PAUSED'),
      )
      .map((session) => ({
        sessionId: session.sessionId,
        deviceName: session.deviceName,
        reservationType: session.reservationType,
        threeBallPriceMd: session.threeBallPriceMd,
      })),
  );
}

export type RunningSelectedSession = Pick<
  OutstandingSessionRow,
  'sessionId' | 'deviceName' | 'reservationType' | 'threeBallPriceMd'
>;
